import { Radio, Select } from '@mantine/core';
import { TimePicker } from '@mantine/dates';
import { schemaResolver, useForm } from '@mantine/form';
import { AnimatePresence, motion } from 'motion/react';
import { FC } from 'react';

import { SeriesPlanningSelector } from '@/components/AppointmentScheduler/series-planning-selector.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import { SERIES_AUTO_SUGGESTION_DEFAULTS } from '@/constants/series-auto-suggestion.ts';

import {
    CollectionConfiguration,
    IntervalType,
    PreferredAvailability,
    SeriesPlanningMode,
} from '@/models/appointment/Appointment.ts';

import { CollectionConfigFormValues, collection_configuration_schema } from '@/schemas/BookAppointmentForm.schema.ts';

import { useBookingStore } from '@/stores/bookingStore.ts';

import { dayjs } from '@/utils/dayjsSetup.ts';
import {
    collection_appointment_count_options,
    collection_frequency_options,
} from '@/utils/practitioner/bookingFormOptionsUtils.ts';

const EXPAND_COLLAPSE_ANIMATION = {
    initial: { opacity: 0, height: 0 },
    animate: { opacity: 1, height: 'auto' },
    exit: { opacity: 0, height: 0 },
    transition: { duration: 0.25, ease: 'easeInOut' as const },
    style: { overflow: 'hidden' as const },
};

export const CollectionConfigurator: FC = () => {
    const collectionConfiguration = useBookingStore((state) => state.collection_configuration);
    const collectionPastAppointments = useBookingStore((state) => state.collectionPastAppointments);
    const isBookingLoading = useBookingStore((state) => state.is_booking_loading);

    // Filter appointment count options if collection_appointments exist
    const filteredAppointmentCountOptions =
        collectionPastAppointments && collectionPastAppointments.length > 0
            ? collection_appointment_count_options.filter(
                  (option) => Number(option.value) >= collectionPastAppointments.length
              )
            : collection_appointment_count_options;

    const form = useForm<CollectionConfigFormValues>({
        mode: 'controlled',
        initialValues: collectionConfiguration ?? {
            start_date: dayjs().format('YYYY-MM-DD'),
            interval_type: IntervalType.WEEKLY,
            interval_frequency: 1,
            number_of_appointments: 6,
            ...SERIES_AUTO_SUGGESTION_DEFAULTS,
        },
        validate: schemaResolver(collection_configuration_schema, { sync: true }),
        onValuesChange: (values) => {
            useBookingStore.setState({ collection_configuration: values as CollectionConfiguration });
        },
    });

    const handlePreferredAvailabilityChange = (value: string): void => {
        const availability = value as PreferredAvailability;
        form.setFieldValue('preferred_availability', availability);
        if (availability === PreferredAvailability.FIXED) {
            if (!form.values.preferred_time) {
                form.setFieldValue('preferred_time', dayjs().format('HH:mm'));
            }
        } else {
            form.setFieldValue('preferred_time', undefined);
        }
    };

    const handlePlanningModeChange = (mode: SeriesPlanningMode): void => {
        form.setFieldValue('planning_mode', mode);
    };

    return (
        <fieldset disabled={isBookingLoading} className="flex min-w-0 flex-col gap-4 border-0 p-0">
            <div>
                <Paragraph className="text-sm font-medium">Terminserie konfigurieren</Paragraph>
                <Paragraph className="text-xs text-slate-500">
                    Legen Sie Intervall und Zeitraum der Serie fest
                </Paragraph>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <Select
                    label="Anzahl der Termine"
                    searchable
                    allowDeselect={false}
                    data={filteredAppointmentCountOptions}
                    value={String(form.values.number_of_appointments)}
                    onChange={(value) => form.setFieldValue('number_of_appointments', value ? Number(value) : 2)}
                />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="rounded-md border border-slate-200 p-4">
                    <Radio.Group
                        label="Intervall"
                        value={form.values.interval_type}
                        onChange={(value) => form.setFieldValue('interval_type', value as IntervalType)}
                    >
                        <div className="mt-3 flex flex-col gap-3">
                            <Radio className="cursor-pointer" value={IntervalType.DAILY} label="Täglich" />
                            <Radio className="cursor-pointer" value={IntervalType.WEEKLY} label="Wöchentlich" />
                            <Radio className="cursor-pointer" value={IntervalType.MONTHLY} label="Monatlich" />
                        </div>
                    </Radio.Group>
                    <div className="mt-3 flex items-center gap-2 text-sm">
                        <span>Alle</span>
                        <Select
                            size="xs"
                            searchable
                            allowDeselect={false}
                            className="w-[3.75rem]"
                            data={collection_frequency_options}
                            value={String(form.values.interval_frequency)}
                            onChange={(value) => form.setFieldValue('interval_frequency', value ? Number(value) : 1)}
                        />
                        <span>
                            {form.values.interval_type === IntervalType.DAILY && 'Tag(e)'}
                            {form.values.interval_type === IntervalType.WEEKLY && 'Woche(n)'}
                            {form.values.interval_type === IntervalType.MONTHLY && 'Monat(e)'}
                        </span>
                    </div>
                </div>

                <div className="rounded-md border border-slate-200 p-4">
                    <Radio.Group
                        label="Bevorzugte Tageszeit"
                        value={form.values.preferred_availability}
                        onChange={handlePreferredAvailabilityChange}
                    >
                        <div className="mt-3 flex flex-col gap-3">
                            <Radio
                                className="cursor-pointer"
                                value={PreferredAvailability.MORNING}
                                label="Vormittag (8-12 Uhr)"
                            />
                            <Radio
                                className="cursor-pointer"
                                value={PreferredAvailability.AFTERNOON}
                                label="Nachmittag (13-18 Uhr)"
                            />
                            <Radio className="cursor-pointer" value={PreferredAvailability.FLEXIBLE} label="Flexibel" />
                            <Radio
                                className="cursor-pointer"
                                value={PreferredAvailability.FIXED}
                                label="Bestimmte Uhrzeit"
                            />
                        </div>
                    </Radio.Group>
                    <AnimatePresence>
                        {form.values.preferred_availability === PreferredAvailability.FIXED && (
                            <motion.div key="time-input" {...EXPAND_COLLAPSE_ANIMATION}>
                                <TimePicker
                                    label="Uhrzeit"
                                    className="mt-3"
                                    value={form.values.preferred_time}
                                    onChange={(value) => form.setFieldValue('preferred_time', value)}
                                />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            <SeriesPlanningSelector
                planningMode={form.values.planning_mode ?? SeriesPlanningMode.START_DATE}
                onPlanningModeChange={handlePlanningModeChange}
                startDate={form.values.start_date}
                onStartDateChange={(date) => form.setFieldValue('start_date', date)}
            />
        </fieldset>
    );
};
